// Components
import { forwardRef, MouseEvent, TouchEvent, useState } from 'react'
import Button from '../../shared/Button'
import { Text } from '../..'
import { IconHeart, IconHeartEmpty } from '../../shared/Icon'
import { StatusBadge } from '../StatusBadge'
import { Card } from './BaseCard'
import styles from './ListingCard.module.scss'
import { SearchListing } from '../../../types/listings'
import { MlsAbbrevAndNumber } from '../Compliance/MlsAbbrevAndNumber'
import { ShareButton } from './ShareButton'
import {
  useFavoriteListingClickHandler,
  useIsFavoritedListing,
} from '../../../hooks/listings'
import { useDispatch, useSelector } from 'react-redux'
import { addToast } from '../../../actions/toasts'
import { toggleFavoriteListing } from '../../../actions/favoriteListings'
import { selectCustomerId } from '../../../store/selectors/customerProfile'
import { defineMessages, useIntl } from 'react-intl'
import { routeToFavoriteListings } from '../../../store/selectors/routes'
import history from '../../../history'
import { selectClientIsGuest } from '../../../store/selectors/client'

export interface ListingCardProps {
  listing: SearchListing
  allowFavorite?: boolean
  focused?: boolean
  isSlim?: boolean
  className?: string
  position?: number
}

export const ListingCard = forwardRef<HTMLDivElement, ListingCardProps>(
  (
    {
      listing,
      allowFavorite = true,
      focused = false,
      isSlim = false,
      className,
      position = -1,
    }: ListingCardProps,
    ref
  ) => {
    const fullAddress = `${listing.address.number} ${listing.address.street} ${listing.address.city}, ${listing.address.stateOrProvince} ${listing.address.postalCode}`
    // fix for allowing users to scroll listings on mobile and
    // retain single tap to navigate to the listing details page
    const [isTouchMoving, setIsTouchMoving] = useState(false)
    const mediumFont = isSlim ? 's' : 'm'
    const smallFont = isSlim ? 'xs' : 's'
    const favoriteClick = useFavoriteListingClickHandler()
    const isFavorite: boolean = useIsFavoritedListing(listing)
    const isGuest = useSelector(selectClientIsGuest)
    const dispatch = useDispatch()
    const customerId = useSelector(selectCustomerId)
    const intl = useIntl()
    const favoriteListingsUrl = routeToFavoriteListings(customerId)

    const MSG = defineMessages({
      baths: {
        id: 'Baths.lowercase',
        defaultMessage: '{num, plural, one {bath} other {baths}}',
      },
      ft: { id: 'ft', defaultMessage: 'ft' },
      beds: {
        id: 'Beds.lowercase',
        defaultMessage: '{num, plural, one {bed} other {beds}}',
      },
      added: {
        id: 'Listing.Favorite.Added',
        defaultMessage: 'Added to Favorites',
      },
      favorites: {
        id: 'General.favorites.uppercase',
        defaultMessage: 'Favorites',
      },
      view: {
        id: 'General.view',
        defaultMessage: 'View',
      },
    })

    const handleFavoriteClick = (
      e: MouseEvent<HTMLElement> | TouchEvent<HTMLButtonElement>
    ) => {
      e.stopPropagation()
      favoriteClick?.(e, listing)
      if (isFavorite && !isGuest) {
        dispatch(
          addToast({
            listingId: listing.id,
            position: position,
            message: intl.formatMessage(
              { id: 'Listing.Favorite.Status' },
              { status: isFavorite?.toString() }
            ),
            action: intl.formatMessage({ id: 'Undo' }),
            onClose: () => {
              dispatch(toggleFavoriteListing({ listing, customerId, position }))
            },
          })
        )
      } else if (!isGuest) {
        dispatch(
          addToast({
            listingId: listing.id,
            position: position,

            message: intl.formatMessage(MSG.added),
            action: intl.formatMessage(MSG.view),
            onClose: () => {
              history.push(favoriteListingsUrl)
            },
          })
        )
      }
    }

    return (
      <Card ref={ref} focused={focused} slim={isSlim} className={className}>
        <Card.Status>
          <StatusBadge status={listing.status} />
        </Card.Status>
        <Card.Image
          image={listing?.thumbnailImageFileNames[0]}
          alt={fullAddress}
          isSlim={isSlim}
        />
        <Card.Body>
          <div className={styles.bodyHeader}>
            <div>
              <Text
                element='h2'
                size='l'
                align='left'
                weight='bold'
                variant='neutralCool'
                variantCount='900'
              >
                {listing.priceFormatted}
              </Text>

              <div>
                <div className={styles.listingStat}>
                  <Text
                    element='span'
                    variant='neutralCool'
                    variantCount='700'
                    size={mediumFont}
                  >
                    {listing.bedroomsCount}{' '}
                  </Text>
                  <Text
                    element='span'
                    weight='semibold'
                    variant='neutralCool'
                    variantCount='800'
                    size={smallFont}
                  >
                    {intl.formatMessage(MSG.beds, {
                      num: listing.bedroomsCount,
                    })}
                  </Text>
                </div>
                <div className={styles.listingStat}>
                  <Text
                    element='span'
                    variant='neutralCool'
                    variantCount='700'
                    size={mediumFont}
                  >
                    {listing.bathroomsCount}{' '}
                  </Text>
                  <Text
                    element='span'
                    weight='semibold'
                    variant='neutralCool'
                    variantCount='800'
                    size={smallFont}
                  >
                    {intl.formatMessage(MSG.baths, {
                      num: listing.bathroomsCount,
                    })}
                  </Text>
                </div>
                <div className={styles.listingStat}>
                  <Text
                    element='span'
                    variant='neutralCool'
                    variantCount='700'
                    size={mediumFont}
                  >
                    {Math.round(listing.sqftTotal).toLocaleString()}{' '}
                  </Text>
                  <Text
                    element='span'
                    weight='semibold'
                    variant='neutralCool'
                    variantCount='800'
                    size={smallFont}
                  >
                    {intl.formatMessage(MSG.ft)}
                    {'\u00B2'}
                  </Text>
                </div>
              </div>
            </div>

            <div className={styles.buttonIconRow}>
              <ShareButton
                buttonClassName={styles.actionButton}
                listing={listing}
              />

              {allowFavorite && (
                <Button
                  className={styles.actionButton}
                  data-qa='favoriteButton'
                  iconStart={isFavorite ? IconHeart : IconHeartEmpty}
                  aria-label='favorite toggle'
                  aria-pressed={isFavorite}
                  onClick={e => handleFavoriteClick(e)}
                  onTouchMove={() => setIsTouchMoving(true)}
                  onTouchEnd={e =>
                    isTouchMoving
                      ? setIsTouchMoving(false)
                      : handleFavoriteClick(e)
                  }
                />
              )}
            </div>
          </div>
          <div className={styles.bodyContent}>
            <div>
              <Text
                element='span'
                size={smallFont}
                variant='neutralCool'
                variantCount='600'
              >
                {listing.address.number} {listing.address.street}
              </Text>
            </div>
            <div>
              <Text
                element='span'
                size={smallFont}
                variant='neutralCool'
                variantCount='600'
              >
                {listing.address.city}, {listing.address.stateOrProvince}{' '}
              </Text>
              {listing.address.postalCode && (
                <Text
                  size={smallFont}
                  element='span'
                  variant='neutralCool'
                  variantCount='600'
                >
                  {listing.address.postalCode}
                </Text>
              )}
            </div>
            <MlsAbbrevAndNumber
              className={styles.mlsAbbrevAndNumber}
              listing={listing}
            />
          </div>
        </Card.Body>
      </Card>
    )
  }
)
